import { Button, ColumnTable, Loading, Table } from '@octano/global-ui';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DisplayError from '../../../../components/info/DisplayError';
import { TranslationsKeys } from '../../../../locales/translations';
import { usePaymentHubCollectionsWithoutSalenoteLoader } from '../loaders/PaymentHubCollectionsWithoutSalenoteData';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  Collection,
  IdentificationDocument,
} from '../../interfaces/collection';
import { usePaymentHubFormFieldsLoader } from '../../loaders/PaymentHubCollectionsWithoutSalenoteData';
import { CollectionTypeCode } from '../../enums/collection-type-code.enum';
import { moneyFormatter } from '../../../../utils/currency';
import { IdentificationDocumentCode } from '../../enums/identification-document-code.enum';
import { format } from 'rut.js';
import GenerateSalenoteModal, {
  GenerateSalenoteModalMethods,
} from './GenerateSalenoteModal';

dayjs.extend(utc);

const IdentificationTable = () => {
  const generateRef = useRef<GenerateSalenoteModalMethods>(null);
  const { t } = useTranslation(TranslationsKeys.PAYMENT_HUB_NON_SALENOTES);
  const {
    result,
    loading,
    refresh,
  } = usePaymentHubCollectionsWithoutSalenoteLoader();

  const { periods = [], programs = [] } = usePaymentHubFormFieldsLoader();

  const items = useMemo(() => {
    if (!result?.success) {
      return [];
    }
    return result.data ?? [];
  }, [result]);

  const handleGenerate = useCallback((row: Collection) => {
    generateRef?.current?.open(row);
  }, []);

  const formatAccountDocument = ({
    code,
    document,
    country,
  }: IdentificationDocument) => {
    switch (code) {
      case IdentificationDocumentCode.CL_RUT:
        return format(document);
      case IdentificationDocumentCode.Passport:
        return `${country}|${document}`;
    }
  };

  const formatDetails = useCallback(
    ({ type, installment, maxInstallment }: Collection) => {
      let text = t(`table.concepts.${type}`);

      if (maxInstallment > 1 || type === CollectionTypeCode.Quota) {
        if (maxInstallment <= 0) {
          text += ` (${installment})`;
        } else {
          text += ` (${installment}/${maxInstallment ?? 1})`;
        }
      }
      return text;
    },
    [t],
  );

  const columns = useMemo<ColumnTable<Collection>[]>(
    () => [
      {
        headerText: t(`table.name`),
        columnName: 'student',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => (
          <div>
            <span className="w-100 d-block text-center">
              {row?.student?.fullname}
            </span>
            <span className="w-100 d-block text-center">
              {formatAccountDocument(row?.student?.identificationDocument)}
            </span>
          </div>
        ),
      },
      {
        headerText: t(`table.sustainer`),
        columnName: 'sustainer',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => (
          <div>
            <span className="w-100 d-block text-center">
              {row?.sustainer?.fullname}
            </span>
            <span className="w-100 d-block text-center">
              {formatAccountDocument(row?.sustainer?.identificationDocument)}
            </span>
          </div>
        ),
      },
      {
        headerText: t(`table.matriculationDate`),
        columnName: 'matriculationDate',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) =>
          value ? dayjs(value).format('DD/MM/YYYY') : '-',
      },
      {
        headerText: t(`table.program`),
        columnName: 'program',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) =>
          programs?.find((v) => `${v?.id}` === `${value}`)?.name ??
          value ??
          '-',
      },
      {
        headerText: t(`table.period`),
        columnName: 'period',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) =>
          periods?.find((v) => `${v?.id}` === `${value}`)?.name ?? value ?? '-',
      },
      {
        headerText: t(`table.details`),
        columnName: 'type',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => formatDetails(row),
      },
      {
        headerText: t(`table.type`),
        columnName: 'paymentType',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => t(`table.types.${value}`),
      },
      {
        headerText: t(`table.date`),
        columnName: 'paymentDate',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => dayjs(value).format('DD/MM/YYYY HH:mm'),
      },
      {
        headerText: t(`table.documentType`),
        columnName: 'documentTypeCode',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => t(`table.documentTypes.${value}`),
      },
      {
        headerText: t(`table.paymentGateway`),
        columnName: 'paymentGatewayCode',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => t(`table.gateways.${value}`),
      },
      {
        headerText: t(`table.authorizationCodeId`),
        columnName: 'authorizationCode',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => value,
      },
      {
        headerText: t(`table.contract`),
        columnName: 'contract',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => value,
      },
      {
        headerText: t(`table.salenote`),
        columnName: 'salenote',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => value,
      },
      {
        headerText: t(`table.origin`),
        columnName: 'origin',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => t(`table.origins.${value}`),
      },
      {
        headerText: t(`table.amount`),
        columnName: 'amount',
        tdClassName: 'text-right',
        thClassName: 'text-right',
        cellFormat: ({ value }) => (
          <strong>{moneyFormatter().format(Math.ceil(value))}</strong>
        ),
      },
      {
        columnName: 'id',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: '',
        width: 180,
        cellFormat: ({ row }) => {
          return (
            <Button
              size="sm"
              text={t(`table.actions.generateSalenote`)}
              onClick={() => handleGenerate(row)}
            />
          );
        },
      },
    ],
    [t, handleGenerate, formatDetails, periods, programs],
  );

  if (loading) {
    return <Loading textBody="" />;
  }

  if (!result?.success) {
    return (
      <DisplayError
        title={t('queryErrors.title')}
        textBody={t('queryErrors.description')}
      />
    );
  }

  return (
    <div className="p-5">
      <div className="w-full d-flex align-items-end mb-3">
        <p className="mb-0 flex-fill">{t('tablePrefix')}</p>
      </div>
      <Table
        columns={columns}
        data={items}
        noResultsText={t(`empty.description`)}
      />
      <GenerateSalenoteModal ref={generateRef} onSave={refresh} />
    </div>
  );
};

export default IdentificationTable;
